import { useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import {
  FaCheck, FaTimes, FaInfoCircle, FaSync, FaRupeeSign,
  FaSave, FaPrint, FaTimes as FaClose, FaUserPlus,
  FaCalendarAlt, FaShoppingBag
} from "react-icons/fa";
import { useTranslation } from "react-i18next";

const API = process.env.REACT_APP_API_URL || "http://localhost:5000";

/* ═══════════════════════════════════════════════════
   TOAST COMPONENT  (identical to Adevotees)
═══════════════════════════════════════════════════ */
function Toast({ message, type, onClose }) {
  const { t } = useTranslation();
  useEffect(() => {
    const timer = setTimeout(onClose, 5000);
    return () => clearTimeout(timer);
  }, [onClose]);

  const config = {
    success: { icon: <FaCheck />, label: t("toastBlessings"), accentColor: "#2a6b2a", iconBg: "rgba(42,107,42,0.12)", borderColor: "rgba(42,107,42,0.3)", symbol: "✦" },
    error:   { icon: <FaTimes />, label: t("toastError"),     accentColor: "#8b1a1a", iconBg: "rgba(139,26,26,0.1)",  borderColor: "rgba(139,26,26,0.28)", symbol: "✗" },
    info:    { icon: <FaInfoCircle />, label: t("toastDivineNotice"), accentColor: "#8a6020", iconBg: "rgba(201,151,58,0.1)", borderColor: "rgba(201,151,58,0.3)", symbol: "ॐ" }
  };
  const c = config[type] || config.info;

  return (
    <div className="adv-toast-container">
      <div className="adv-toast-card" style={{ "--accent": c.accentColor, "--border-color": c.borderColor }}>
        <div className="adv-toast-top-bar" />
        <span className="adv-toast-corner tl" /><span className="adv-toast-corner tr" />
        <span className="adv-toast-corner bl" /><span className="adv-toast-corner br" />
        <div className="adv-toast-medallion" style={{ background: c.iconBg, borderColor: c.borderColor }}>
          <div className="adv-toast-spin-ring" />
          <div className="adv-toast-icon-inner" style={{ color: c.accentColor }}>{c.icon}</div>
        </div>
        <div className="adv-toast-content">
          <div className="adv-toast-label-row">
            <span className="adv-toast-symbol" style={{ color: c.accentColor }}>{c.symbol}</span>
            <h4 className="adv-toast-title" style={{ color: c.accentColor }}>{c.label}</h4>
            <span className="adv-toast-symbol" style={{ color: c.accentColor }}>{c.symbol}</span>
          </div>
          <div className="adv-toast-rule" style={{ background: `linear-gradient(90deg,transparent,${c.accentColor}55,transparent)` }} />
          <p className="adv-toast-message">{message}</p>
        </div>
        <button className="adv-toast-close" onClick={onClose} style={{ color: c.accentColor }}>✕</button>
        <div className="adv-toast-bottom-rule" />
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════
   BOOKING FORM MODAL  (styled like EditDevoteeModal)
═══════════════════════════════════════════════════ */
function BookingFormModal({ category, onClose, onBooked, showToast }) {
  const { t } = useTranslation();

  const [formData, setFormData] = useState({
    mobile_number: "",
    booking_date: new Date().toISOString().split("T")[0],
    total_persons: 1,
    persons: [{ name: "", nakshathram: "" }],
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const handlePersonChange = (index, field, value) => {
    const newPersons = [...formData.persons];
    newPersons[index][field] = value;
    setFormData(prev => ({ ...prev, persons: newPersons }));
    if (errors[`p${index}_${field}`]) {
      setErrors(prev => { const n = { ...prev }; delete n[`p${index}_${field}`]; return n; });
    }
  };

  const addPersonField = () => {
    setFormData(prev => ({
      ...prev,
      persons: [...prev.persons, { name: "", nakshathram: "" }],
      total_persons: prev.persons.length + 1,
    }));
  };

  const removePersonField = (index) => {
    if (formData.persons.length <= 1) return;
    setFormData(prev => ({
      ...prev,
      persons: prev.persons.filter((_, i) => i !== index),
      total_persons: prev.persons.length - 1,
    }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => { const n = { ...prev }; delete n[name]; return n; });
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.mobile_number || !/^\d{10}$/.test(formData.mobile_number))
      newErrors.mobile_number = "Valid 10-digit mobile required";
    formData.persons.forEach((p, i) => {
      if (!p.name.trim()) newErrors[`p${i}_name`] = "Name is required";
      if (!p.nakshathram.trim()) newErrors[`p${i}_nakshathram`] = "Nakshathram is required";
    });
    if (!formData.booking_date) newErrors.booking_date = "Date is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setLoading(true);
    try {
      const payload = {
        mobile_number: formData.mobile_number,
        vazhipadu_id: category.vazhipadu_id,
        booking_date: formData.booking_date,
        total_persons: formData.total_persons,
        persons: formData.persons,
        amount: (parseFloat(category.amount) * formData.total_persons).toFixed(2),
        payment_mode: "cash",
      };
      const res = await fetch(`${API}/api/admin/bookings`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Booking failed");
      onBooked(data);
      showToast("Booking successful!", "success");
      onClose();
    } catch (err) {
      showToast(err.message, "error");
    } finally {
      setLoading(false);
    }
  };

  return createPortal(
    <div className="adv-modal-overlay" onClick={onClose}>
      <div className="adv-regal-modal adv-edit-regal" onClick={e => e.stopPropagation()}>
        <div className="adv-rm-topglow" />
        <span className="adv-cm-corner tl" /><span className="adv-cm-corner tr" />
        <span className="adv-cm-corner bl" /><span className="adv-cm-corner br" />

        {/* Header */}
        <div className="adv-rm-header">
          <div className="adv-rm-header-bg" />
          <div className="adv-rm-header-inner">
            <div className="adv-rm-header-icon-wrap">
              <div className="adv-rm-hicon-ring" />
              <FaCalendarAlt className="adv-rm-hicon" />
            </div>
            <div className="adv-rm-header-eyebrow">✦ {t("bkgModEyebrow")} ✦</div>
            <h2 className="adv-rm-title">{t("bkgModTitle")}</h2>
            <p className="adv-rm-name-tag">{category.name}</p>
          </div>
          <button className="adv-rm-close" onClick={onClose}><FaClose /></button>
        </div>
        <div className="adv-rm-rule"><span className="adv-rm-rule-ornament">❖</span></div>

        {/* Body */}
        <div className="adv-edit-body">
          <div className="adv-edit-section-label">✦ {t("bkgSecBooking")}</div>
          <div className="adv-edit-grid">
            {/* Mobile */}
            <div className="adv-edit-field full-width">
              <label className="adv-edit-label">{t("lblMobile")} <span className="adv-req">*</span></label>
              <div className={`adv-edit-wrap ${errors.mobile_number ? "err" : ""}`}>
                <span className="adv-edit-prefix">🇮🇳 +91</span>
                <div className="adv-edit-sep" />
                <input
                  type="tel" name="mobile_number" className="adv-edit-input"
                  placeholder={t("bkgPhMobile")} maxLength="10"
                  value={formData.mobile_number} onChange={handleChange}
                />
              </div>
              {errors.mobile_number && <span className="adv-edit-err">{t("errMobile")}</span>}
            </div>

            {/* Date */}
            <div className="adv-edit-field">
              <label className="adv-edit-label">{t("bkgLblDate")} <span className="adv-req">*</span></label>
              <div className={`adv-edit-wrap ${errors.booking_date ? "err" : ""}`}>
                <input
                  type="date" name="booking_date" className="adv-edit-input"
                  value={formData.booking_date} onChange={handleChange}
                  min={new Date().toISOString().split("T")[0]}
                />
              </div>
              {errors.booking_date && <span className="adv-edit-err">{t("errDate")}</span>}
            </div>

            {/* Amount */}
            <div className="adv-edit-field">
              <label className="adv-edit-label">{t("bkgLblAmount")}</label>
              <div className="adv-edit-wrap adv-edit-wrap-readonly">
                <span className="adv-edit-prefix"><FaRupeeSign /></span>
                <span className="adv-edit-input adv-readonly-val">
                  {(parseFloat(category.amount) * formData.total_persons).toFixed(2)}
                </span>
              </div>
            </div>
          </div>

          {/* Devotee rows */}
          <div className="adv-edit-section-label" style={{ marginTop: 20 }}>✦ {t("bkgSecDevotee")}</div>

          {formData.persons.map((person, index) => (
            <div key={index} className="adv-person-block">
              <div className="adv-person-block-header">
                <span className="adv-person-block-title">{t("bkgPerson")} {index + 1}</span>
                {index > 0 && (
                  <button className="adv-person-remove" onClick={() => removePersonField(index)}>
                    <FaTimes /> {t("bkgBtnRemove")}
                  </button>
                )}
              </div>
              <div className="adv-edit-grid">
                <div className="adv-edit-field">
                  <label className="adv-edit-label">{t("bkgLblName")} <span className="adv-req">*</span></label>
                  <div className={`adv-edit-wrap ${errors[`p${index}_name`] ? "err" : ""}`}>
                    <input
                      type="text" className="adv-edit-input" placeholder={t("bkgPhName")}
                      value={person.name}
                      onChange={e => handlePersonChange(index, "name", e.target.value)}
                    />
                  </div>
                  {errors[`p${index}_name`] && <span className="adv-edit-err">{t("errName")}</span>}
                </div>
                <div className="adv-edit-field">
                  <label className="adv-edit-label">{t("bkgLblNakshathram")} <span className="adv-req">*</span></label>
                  <div className={`adv-edit-wrap adv-select-wrap ${errors[`p${index}_nakshathram`] ? "err" : ""}`}>
                    <select
                      className="adv-edit-input adv-nakshathram-select"
                      value={person.nakshathram}
                      onChange={e => handlePersonChange(index, "nakshathram", e.target.value)}
                    >
                      <option value="">— നക്ഷത്രം തിരഞ്ഞെടുക്കുക / Select Birth Star —</option>
                      <option value="Aswathy / അശ്വതി">Aswathy / അശ്വതി</option>
                      <option value="Bharani / ഭരണി">Bharani / ഭരണി</option>
                      <option value="Karthika / കാർത്തിക">Karthika / കാർത്തിക</option>
                      <option value="Rohini / രോഹിണി">Rohini / രോഹിണി</option>
                      <option value="Makayiram / മകയിരം">Makayiram / മകയിരം</option>
                      <option value="Thiruvathira / തിരുവാതിര">Thiruvathira / തിരുവാതിര</option>
                      <option value="Punartham / പുണർതം">Punartham / പുണർതം</option>
                      <option value="Pooyam / പൂയം">Pooyam / പൂയം</option>
                      <option value="Ayilyam / ആയില്യം">Ayilyam / ആയില്യം</option>
                      <option value="Makam / മകം">Makam / മകം</option>
                      <option value="Pooram / പൂരം">Pooram / പൂരം</option>
                      <option value="Uthram / ഉത്രം">Uthram / ഉത്രം</option>
                      <option value="Atham / അത്തം">Atham / അത്തം</option>
                      <option value="Chithira / ചിത്തിര">Chithira / ചിത്തിര</option>
                      <option value="Chothi / ചോതി">Chothi / ചോതി</option>
                      <option value="Vishakham / വിശാഖം">Vishakham / വിശാഖം</option>
                      <option value="Anizham / അനിഴം">Anizham / അനിഴം</option>
                      <option value="Thrikketta / തൃക്കേട്ട">Thrikketta / തൃക്കേട്ട</option>
                      <option value="Moolam / മൂലം">Moolam / മൂലം</option>
                      <option value="Pooradam / പൂരാടം">Pooradam / പൂരാടം</option>
                      <option value="Uthradam / ഉത്രാടം">Uthradam / ഉത്രാടം</option>
                      <option value="Thiruvonam / തിരുവോണം">Thiruvonam / തിരുവോണം</option>
                      <option value="Avittam / അവിട്ടം">Avittam / അവിട്ടം</option>
                      <option value="Chathayam / ചതയം">Chathayam / ചതയം</option>
                      <option value="Pooruruttathi / പൂരുരുട്ടാതി">Pooruruttathi / പൂരുരുട്ടാതി</option>
                      <option value="Uthrattathi / ഉത്രട്ടാതി">Uthrattathi / ഉത്രട്ടാതി</option>
                      <option value="Revathi / രേവതി">Revathi / രേവതി</option>
                    </select>
                    <span className="adv-select-arrow">▾</span>
                  </div>
                  {errors[`p${index}_nakshathram`] && <span className="adv-edit-err">{t("errNakshathram")}</span>}
                </div>
              </div>
            </div>
          ))}

          <button className="adv-add-person-btn" onClick={addPersonField}>
            <FaUserPlus /> {t("bkgBtnAddPerson")}
          </button>

          <div className="adv-edit-actions" style={{ justifyContent: "center" }}>
            <button className="adv-btn adv-btn-ghost" onClick={onClose} disabled={loading}>
              {t("btnCancel") || "Cancel"}
            </button>
            <button
              className="adv-edit-save-btn"
              onClick={handleSubmit}
              disabled={loading || Object.keys(errors).length > 0}
            >
              {loading
                ? <><span className="adv-edit-saving-spinner" />{t("bkgRegistering")}…</>
                : <><FaSave /> {t("catBtnCreate")}</>
              }
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

/* ═══════════════════════════════════════════════════
   RECEIPT MODAL  — enhanced with No., auto-download,
   cancel-confirm, PDF download & thermal print
═══════════════════════════════════════════════════ */
function ReceiptModal({ bookingId, onClose }) {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const receiptRef = useRef(null);

  useEffect(() => {
    const fetchReceipt = async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API}/api/admin/bookings/${bookingId}/receipt`);
        const json = await res.json();
        setData(json);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    if (bookingId) fetchReceipt();
  }, [bookingId, API]);

  const handlePrint = () => {
    const printWin = window.open("", "_blank", "width=380,height=700");
    const total = (parseFloat(data.booking.category_amount) * data.booking.total_persons).toFixed(2);
    const bookingDate = new Date(data.booking.booking_date).toLocaleDateString("ml-IN", { day:"2-digit", month:"long", year:"numeric" });
    const now = new Date().toLocaleString("en-IN", { day:"2-digit", month:"short", year:"numeric", hour:"2-digit", minute:"2-digit" });
    const personRows = data.persons.map((p, i) => `
      <tr class="${i % 2 === 0 ? "row-even" : "row-odd"}">
        <td class="p-no">${i + 1}</td>
        <td class="p-name">${p.name}</td>
        <td class="p-star">${p.nakshathram || "—"}</td>
      </tr>
    `).join("");
    printWin.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8"/>
        <title>Receipt - ${data.booking.receipt_number}</title>
        <link href="https://fonts.googleapis.com/css2?family=Noto+Serif+Malayalam:wght@400;600;700&display=swap" rel="stylesheet"/>
        <style>
          * { margin:0; padding:0; box-sizing:border-box; }
          html, body { width:100%; max-width:100%; overflow-x:hidden; }
          body {
            font-family: 'Noto Serif Malayalam', serif;
            font-size: 12px;
            background: #fff;
            color: #000;
          }
          .wrap { width:80mm; max-width:80mm; padding: 4mm 4mm 8mm; overflow:hidden; margin: 0 auto; }

          /* ── RULES ── */
          .rule-d  { border:none; border-top:1px dashed #aaa; margin:2.5mm 0; }
          .rule-s  { border:none; border-top:1px solid #000; margin:1.5mm 0; }
          .rule-db { border:none; border-top:2px double #000; margin:2.5mm 0; }

          /* ── HEADER ── */
          .header { text-align:center; padding-bottom:2mm; }
          .logo-circle {
            width:18mm; height:18mm; border-radius:50%;
            border:2px solid #000;
            display:inline-flex; align-items:center; justify-content:center;
            overflow:hidden; margin-bottom:2mm;
          }
          .logo-circle img { width:15mm; height:15mm; object-fit:contain; }
          .logo-om { font-size:20px; line-height:1; }
          .t-ml      { font-size:12.5px; font-weight:700; line-height:1.55; }
          .t-addr-ml { font-size:9px; color:#333; margin-top:0.8mm; }
          .t-divider { font-size:8px; color:#aaa; margin:1.5mm 0 1mm; letter-spacing:2px; }
          .t-en      { font-size:7.5px; font-weight:700; text-transform:uppercase; letter-spacing:0.3px; color:#222; }
          .t-addr-en { font-size:7px; color:#555; margin-top:0.5mm; }

          /* ── TITLE BAND ── */
          .title-band {
            border-top:2px solid #000; border-bottom:2px solid #000;
            text-align:center;
            padding:2.5mm 2mm;
            margin:3mm 0 2.5mm;
            line-height:1.6;
          }
          .title-ml  { font-size:12px; font-weight:700; display:block; color:#000; }
          .title-en  { font-size:7px; font-weight:400; letter-spacing:1.5px; text-transform:uppercase; color:#666; display:block; margin-top:0.5mm; }

          /* ── RECEIPT NO BOX ── */
          .rcpt-box {
            border:1.5px solid #000;
            display:table; width:100%;
            padding:1.5mm 2mm;
            margin-bottom:2.5mm;
          }
          .rcpt-box-inner { display:table-row; }
          .rcpt-lbl-cell  { display:table-cell; vertical-align:middle; }
          .rcpt-lbl-ml    { font-size:8px; color:#555; display:block; }
          .rcpt-lbl-en    { font-size:7px; color:#999; text-transform:uppercase; letter-spacing:0.5px; display:block; }
          .rcpt-val-cell  { display:table-cell; text-align:right; vertical-align:middle; font-size:13px; font-weight:700; letter-spacing:0.5px; }

          /* ── INFO TABLE ── */
          .info-tbl { width:100%; border-collapse:collapse; margin-bottom:1mm; }
          .info-tbl td { padding:1.3mm 0; vertical-align:top; }
          .lbl-cell { width:40%; }
          .lbl-ml   { font-size:9px; color:#333; display:block; line-height:1.5; }
          .lbl-en   { font-size:7px; color:#888; text-transform:uppercase; letter-spacing:0.3px; display:block; }
          .val-cell { font-weight:700; font-size:11px; color:#000; }
          .val-cell.sm { font-size:10px; }

          /* ── CATEGORY BOX ── */
          .cat-outer { border-top:2px solid #000; border-bottom:2px solid #000; margin:2.5mm 0; }
          .cat-inner { border-top:1px solid #bbb; border-bottom:1px solid #bbb; margin:1.5px 0; padding:2mm 2mm; text-align:center; }
          .cat-tag-ml { font-size:8.5px; color:#444; display:block; margin-bottom:1mm; }
          .cat-tag-en { font-size:7px; color:#888; text-transform:uppercase; letter-spacing:0.5px; display:block; margin-bottom:1.5mm; }
          .cat-name   { font-size:14px; font-weight:700; line-height:1.45; }

          /* ── DEVOTEE SECTION ── */
          .dev-head { border-top:1px solid #000; border-bottom:1px solid #000; padding:1.5mm 0; margin:2.5mm 0 1.5mm; text-align:center; }
          .dev-head-ml { font-size:9px; font-weight:700; display:block; }
          .dev-head-en { font-size:7px; color:#666; text-transform:uppercase; letter-spacing:0.5px; display:block; margin-top:0.3mm; }

          .dev-tbl { width:100%; border-collapse:collapse; table-layout:fixed; }
          .dev-tbl col.c-no   { width:8mm; }
          .dev-tbl col.c-name { width:38mm; }
          .dev-tbl col.c-star { width:27mm; }
          .dev-tbl thead th {
            padding:1.5mm 1mm; border-bottom:1px dashed #000;
            text-align:left; vertical-align:bottom;
          }
          .th-ml { font-size:8px; font-weight:700; display:block; }
          .th-en { font-size:6.5px; color:#888; text-transform:uppercase; letter-spacing:0.3px; display:block; }
          .th-no   { text-align:center !important; }
          .th-star { text-align:right !important; }
          .dev-tbl tbody tr { border-bottom:1px dashed #ddd; }
          .dev-tbl tbody td { padding:1.8mm 1mm; vertical-align:middle; overflow:hidden; }
          .p-no    { text-align:center; color:#555; font-size:9px; font-weight:700; }
          .p-name  { font-size:10.5px; font-weight:700; word-break:break-word; }
          .p-star  { text-align:right; font-size:9px; font-style:italic; color:#333; word-break:break-word; }
          .row-even td { background:#fff; }
          .row-odd  td { background:#f7f7f7; }

          /* ── TOTAL ── */
          .total-band { border-top:2px solid #000; border-bottom:2px solid #000; padding:2mm 2.5mm; margin:2.5mm 0; display:table; width:100%; }
          .total-inner { display:table-row; }
          .total-lbl-cell { display:table-cell; vertical-align:middle; }
          .total-lbl-ml   { font-size:10px; font-weight:700; display:block; color:#000; }
          .total-lbl-en   { font-size:7px; font-weight:400; color:#666; text-transform:uppercase; letter-spacing:0.5px; display:block; margin-top:0.5mm; }
          .total-amt-cell { display:table-cell; text-align:right; vertical-align:middle; font-size:18px; font-weight:700; color:#000; }

          /* ── PAYMENT ROW ── */
          .pay-row { display:table; width:100%; font-size:9px; color:#555; padding:0 0.5mm; margin-bottom:2mm; }
          .pay-row span { display:table-cell; }
          .pay-row span:last-child { text-align:right; font-weight:700; color:#000; }

          /* ── FOOTER ── */
          .footer { text-align:center; margin-top:3mm; }
          .bless-box { border:1px dashed #bbb; padding:2mm 2.5mm; margin-bottom:2.5mm; font-size:9.5px; line-height:1.8; color:#222; }
          .bless-en  { font-size:7.5px; color:#888; font-style:italic; margin-top:1mm; }

          /* BARCODE — fixed width, no overflow */
          .barcode-wrap { width:100%; overflow:hidden; margin:2mm 0 1mm; }
          .barcode {
            display:block; width:100%; text-align:center;
            font-size:14px; color:#000;
            letter-spacing:-1px; line-height:1;
            overflow:hidden; white-space:nowrap;
            font-family: 'Courier New', monospace;
          }
          .rcpt-ref   { font-size:7.5px; color:#888; letter-spacing:1px; margin-bottom:2mm; word-break:break-all; }
          .print-time { font-size:7.5px; color:#666; border-top:1px dashed #ccc; padding-top:1.5mm; margin-top:1mm; }

          @page { margin:10mm; size:A4 portrait; }
          @media print {
            body { -webkit-print-color-adjust:exact; print-color-adjust:exact; }
            .page-center { display:flex; justify-content:center; }
          }
        </style>
      </head>
      <body>
      <div class="wrap">

        <!-- HEADER -->
        <div class="header">
          <div class="logo-circle">
            <img src="${window.location.origin}/logo.png" alt="Logo"
              onerror="this.parentNode.innerHTML='<span class=\\'logo-om\\'>ॐ</span>'"/>
          </div>
          <div class="t-ml">കൊടുങ്ങല്ലൂർ കാവിൽ ശ്രീ<br/>ഭദ്രകാളി ദേവി ക്ഷേത്രം</div>
          <div class="t-addr-ml">പാണ്ടനാട് · ചെങ്ങന്നൂർ</div>
          <div class="t-divider">— ✦ —</div>
          <div class="t-en">Kodungallur Kavil Sree Bhadrakali Devi Temple</div>
          <div class="t-addr-en">Pandanad, Chengannur</div>
        </div>

        <hr class="rule-db"/>

        <!-- TITLE BAND -->
        <div class="title-band">
          <span class="title-ml">★ &nbsp;വഴിപാട് രസീത്&nbsp; ★</span>
          <span class="title-en">Official Vazhipadu Receipt</span>
        </div>

        <!-- RECEIPT NO -->
        <div class="rcpt-box">
          <div class="rcpt-box-inner">
            <div class="rcpt-lbl-cell">
              <span class="rcpt-lbl-ml">രസീത് നമ്പർ</span>
              <span class="rcpt-lbl-en">Receipt No.</span>
            </div>
            <div class="rcpt-val-cell">${data.booking.receipt_number}</div>
          </div>
        </div>

        <!-- INFO TABLE -->
        <table class="info-tbl">
          <tr>
            <td class="lbl-cell">
              <span class="lbl-ml">തീയതി</span>
              <span class="lbl-en">Date</span>
            </td>
            <td class="val-cell">${bookingDate}</td>
          </tr>
          <tr>
            <td class="lbl-cell">
              <span class="lbl-ml">മൊബൈൽ</span>
              <span class="lbl-en">Mobile</span>
            </td>
            <td class="val-cell">+91 ${data.booking.mobile_number}</td>
          </tr>
          <tr>
            <td class="lbl-cell">
              <span class="lbl-ml">ആൾക്കാർ</span>
              <span class="lbl-en">Persons</span>
            </td>
            <td class="val-cell sm">${data.booking.total_persons} പേർ &nbsp;×&nbsp; ₹${parseFloat(data.booking.category_amount).toFixed(2)}</td>
          </tr>
        </table>

        <hr class="rule-d"/>

        <!-- CATEGORY -->
        <div class="cat-outer">
          <div class="cat-inner">
            <span class="cat-tag-ml">⭐ വഴിപാട്</span>
            <span class="cat-tag-en">Offering Category</span>
            <div class="cat-name">${data.booking.category_name}</div>
          </div>
        </div>

        <!-- DEVOTEES -->
        <div class="dev-head">
          <span class="dev-head-ml">ഭക്തജന വിവരങ്ങൾ</span>
          <span class="dev-head-en">Devotee Details</span>
        </div>
        <table class="dev-tbl">
          <colgroup>
            <col class="c-no"/>
            <col class="c-name"/>
            <col class="c-star"/>
          </colgroup>
          <thead>
            <tr>
              <th class="th-no">
                <span class="th-ml">നം.</span>
                <span class="th-en">No.</span>
              </th>
              <th>
                <span class="th-ml">പേര്</span>
                <span class="th-en">Name</span>
              </th>
              <th class="th-star">
                <span class="th-ml">നക്ഷത്രം</span>
                <span class="th-en">Birth Star</span>
              </th>
            </tr>
          </thead>
          <tbody>${personRows}</tbody>
        </table>

        <hr class="rule-s" style="margin-top:0"/>

        <!-- TOTAL -->
        <div class="total-band">
          <div class="total-inner">
            <div class="total-lbl-cell">
              <span class="total-lbl-ml">മൊത്തം അടച്ച തുക</span>
              <span class="total-lbl-en">Total Amount Paid</span>
            </div>
            <div class="total-amt-cell">₹${total}</div>
          </div>
        </div>

        <!-- PAYMENT MODE -->
        <div class="pay-row">
          <span>പണമടക്കൽ / Payment Mode</span>
          <span>പണം (Cash)</span>
        </div>

        <hr class="rule-db"/>

        <!-- FOOTER -->
        <div class="footer">
          <div class="bless-box">
            ഈശ്വരൻ നിങ്ങൾക്കും കുടുംബത്തിനും<br/>
            സമൃദ്ധിയും അനുഗ്രഹവും നൽകട്ടെ 🙏
            <div class="bless-en">May the Divine bless you and your family</div>
          </div>
          <div class="barcode-wrap">
            <span class="barcode">|||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||</span>
          </div>
          <div class="rcpt-ref">${data.booking.receipt_number}</div>
          <div class="print-time">അച്ചടിച്ചത് / Printed: ${now}</div>
        </div>

      </div>
      </body>
      </html>
    `);
    printWin.document.close();
    printWin.focus();
    setTimeout(() => { printWin.print(); printWin.close(); }, 800);
  };

  const handleDownloadPDF = async () => {
    if (!data) return;
    setDownloading(true);
    try {
      const total = (parseFloat(data.booking.category_amount) * data.booking.total_persons).toFixed(2);
      const bookingDate = new Date(data.booking.booking_date).toLocaleDateString("ml-IN", { day:"2-digit", month:"long", year:"numeric" });
      const now = new Date().toLocaleString("ml-IN", { day:"2-digit", month:"short", year:"numeric", hour:"2-digit", minute:"2-digit" });
      const personsRows = data.persons.map((p, i) => `
        <tr>
          <td style="padding:7px 12px;border-bottom:1px solid #f0e2b0;color:#8a6020;font-weight:700;text-align:center;background:${i%2===0?"#fffdf5":"#fdf6e3"}">${i+1}</td>
          <td style="padding:7px 12px;border-bottom:1px solid #f0e2b0;font-weight:600;color:#1a0404;background:${i%2===0?"#fffdf5":"#fdf6e3"}">${p.name}</td>
          <td style="padding:7px 12px;border-bottom:1px solid #f0e2b0;color:#5a3a1a;font-style:italic;background:${i%2===0?"#fffdf5":"#fdf6e3"}">${p.nakshathram || "—"}</td>
        </tr>
      `).join("");

      // Convert logo to base64 so html2canvas can embed it without CORS issues
      let logoBase64 = "";
      try {
        const logoRes = await fetch(`${window.location.origin}/logo.png`);
        const logoBlob = await logoRes.blob();
        logoBase64 = await new Promise((res) => {
          const reader = new FileReader();
          reader.onloadend = () => res(reader.result);
          reader.readAsDataURL(logoBlob);
        });
      } catch (e) { logoBase64 = ""; }

      // Fetch Noto Serif Malayalam font and embed as base64 so html2canvas renders it
      let fontFaceCSS = "";
      try {
        const toB64 = async (url) => {
          const res = await fetch(url);
          const buf = await res.arrayBuffer();
          const bytes = new Uint8Array(buf);
          let binary = "";
          for (let i = 0; i < bytes.byteLength; i++) binary += String.fromCharCode(bytes[i]);
          return btoa(binary);
        };
        const [regularB64, boldB64] = await Promise.all([
          toB64("https://fonts.gstatic.com/s/notoserifmalayalam/v28/JIAZUU5sdmdP_HMcVcZFcH7DeVBeGVgSMEk2cmVDq1ihUoSby21wTg.woff2"),
          toB64("https://fonts.gstatic.com/s/notoserifmalayalam/v28/JIAZUU5sdmdP_HMcVcZFcH7DeVBeGVgSMEk2cmVDq1ihUoSby21LT4.woff2"),
        ]);
        fontFaceCSS = `
          @font-face { font-family:'NotoML'; font-weight:400; src:url('data:font/woff2;base64,${regularB64}') format('woff2'); }
          @font-face { font-family:'NotoML'; font-weight:700; src:url('data:font/woff2;base64,${boldB64}') format('woff2'); }
          * { font-family:'NotoML',Georgia,serif !important; }
        `;
      } catch(e) {
        // fallback — just use whatever system font has Malayalam glyphs
        fontFaceCSS = "* { font-family: 'Noto Serif Malayalam', 'Noto Sans Malayalam', Arial Unicode MS, serif !important; }";
      }

      const logoImgTag = logoBase64
        ? `<img src="${logoBase64}" alt="Logo" style="width:70px;height:70px;border-radius:50%;border:3px solid #c9973a;object-fit:contain;margin-bottom:10px;display:block;margin-left:auto;margin-right:auto;"/>`
        : `<div style="width:70px;height:70px;border-radius:50%;border:3px solid #c9973a;background:#fdf3e3;font-size:36px;text-align:center;line-height:70px;margin:0 auto 10px;">ॐ</div>`;

      // Build a hidden div with the receipt HTML
      const container = document.createElement("div");
      container.style.cssText = "position:fixed;top:0;left:0;width:794px;background:#fff;font-family:serif;z-index:-1;opacity:0;pointer-events:none;";
      container.innerHTML = `
        <style>${fontFaceCSS}</style>
        <div style="width:794px;min-height:1123px;background:#fff;color:#1a0404;font-family:'NotoML',Georgia,serif;">
          <div style="height:6px;background:#c9973a;"></div>
          <div style="margin:20px 28px 28px;border:2px solid #c9973a;min-height:1060px;">
            <div style="margin:3px;border:1px solid #e8c46a;padding:28px 34px;min-height:1054px;">

              <!-- Header -->
              <div style="text-align:center;padding-bottom:18px;border-bottom:2px solid #c9973a;margin-bottom:18px;">
                ${logoImgTag}
                <div style="font-size:22px;font-weight:700;color:#1a0404;line-height:1.5;">കൊടുങ്ങല്ലൂർ കാവിൽ ശ്രീ ഭദ്രകാളി ദേവി ക്ഷേത്രം</div>
                <div style="font-size:13px;color:#4a1a08;margin-top:3px;">പാണ്ടനാട് · ചെങ്ങന്നൂർ</div>
                <div style="font-size:11px;font-weight:700;color:#3a0c0c;letter-spacing:1.5px;text-transform:uppercase;margin-top:8px;">Kodungallur Kavil Sree Bhadrakali Devi Temple</div>
                <div style="font-size:11px;color:#8a6020;font-style:italic;margin-top:2px;">Pandanad, Chengannur</div>
                <div style="margin-top:10px;">
                  <span style="display:inline-block;background:#c9973a;color:#1a0404;font-size:11px;font-weight:700;padding:6px 24px;">✦ രസീത് ✦</span>
                </div>
              </div>

              <!-- Info table -->
              <table style="width:100%;border-collapse:separate;border-spacing:0 10px;margin-bottom:6px;">
                <tr>
                  <td style="width:50%;padding:6px 10px 6px 13px;border-left:3px solid #c9973a;vertical-align:top;">
                    <div style="margin-bottom:3px;"><span style="font-size:9px;color:#8a6020;font-weight:700;letter-spacing:1px;text-transform:uppercase;">Receipt No.</span> <span style="font-size:9px;color:#8a6020;font-weight:700;">/ രസീത് നം.</span></div>
                    <div style="font-size:16px;font-weight:700;color:#8b1a1a;">${data.booking.receipt_number}</div>
                  </td>
                  <td style="width:50%;padding:6px 10px 6px 13px;border-left:3px solid #c9973a;vertical-align:top;">
                    <div style="margin-bottom:3px;"><span style="font-size:9px;color:#8a6020;font-weight:700;letter-spacing:1px;text-transform:uppercase;">Date</span> <span style="font-size:9px;color:#8a6020;font-weight:700;">/ തീയതി</span></div>
                    <div style="font-size:14px;font-weight:600;color:#1a0404;">${bookingDate}</div>
                  </td>
                </tr>
                <tr>
                  <td style="padding:6px 10px 6px 13px;border-left:3px solid #c9973a;vertical-align:top;">
                    <div style="margin-bottom:3px;"><span style="font-size:9px;color:#8a6020;font-weight:700;letter-spacing:1px;text-transform:uppercase;">Mobile</span> <span style="font-size:9px;color:#8a6020;font-weight:700;">/ മൊബൈൽ</span></div>
                    <div style="font-size:14px;font-weight:600;color:#1a0404;">+91 ${data.booking.mobile_number}</div>
                  </td>
                  <td style="padding:6px 10px 6px 13px;border-left:3px solid #c9973a;vertical-align:top;">
                    <div style="margin-bottom:3px;"><span style="font-size:9px;color:#8a6020;font-weight:700;letter-spacing:1px;text-transform:uppercase;">Persons</span> <span style="font-size:9px;color:#8a6020;font-weight:700;">/ ആൾക്കാർ</span></div>
                    <div style="font-size:14px;font-weight:600;color:#1a0404;">${data.booking.total_persons}</div>
                  </td>
                </tr>
                <tr>
                  <td style="padding:6px 10px 6px 13px;border-left:3px solid #c9973a;vertical-align:top;">
                    <div style="margin-bottom:3px;"><span style="font-size:9px;color:#8a6020;font-weight:700;letter-spacing:1px;text-transform:uppercase;">Rate per Person</span> <span style="font-size:9px;color:#8a6020;font-weight:700;">/ ഒരാൾക്ക്</span></div>
                    <div style="font-size:14px;font-weight:600;color:#1a0404;">₹${parseFloat(data.booking.category_amount).toFixed(2)}</div>
                  </td>
                  <td style="padding:6px 10px 6px 13px;border-left:3px solid #c9973a;vertical-align:top;">
                    <div style="margin-bottom:3px;"><span style="font-size:9px;color:#8a6020;font-weight:700;letter-spacing:1px;text-transform:uppercase;">Payment</span> <span style="font-size:9px;color:#8a6020;font-weight:700;">/ പണമടക്കൽ</span></div>
                    <div style="font-size:14px;font-weight:600;color:#1a0404;">പണം (Cash)</div>
                  </td>
                </tr>
              </table>

              <!-- Category Highlight -->
              <div style="background:#8b1a1a;padding:13px 18px;margin:14px 0;">
                <div style="margin-bottom:5px;"><span style="font-size:9px;color:#e8c46a;font-weight:700;letter-spacing:1px;text-transform:uppercase;">⭐ Offering Category</span> <span style="font-size:9px;color:#e8c46a;font-weight:700;">/ വഴിപാട്</span></div>
                <div style="font-size:18px;font-weight:700;color:#e8c46a;">✦ ${data.booking.category_name} ✦</div>
              </div>

              <!-- Divider -->
              <div style="height:1px;background:#c9973a;margin:16px 0;"></div>

              <!-- Devotee section label -->
              <div style="text-align:center;margin-bottom:10px;">
                <span style="font-size:10px;font-weight:700;color:#8a6020;letter-spacing:2px;text-transform:uppercase;">── Devotee Details</span> <span style="font-size:10px;font-weight:700;color:#8a6020;">/ ഭക്തജന വിവരങ്ങൾ ──</span>
              </div>

              <!-- Devotee Table -->
              <table style="width:100%;border-collapse:collapse;">
                <thead>
                  <tr style="background:#1a0404;">
                    <th style="font-size:10px;color:#e8c46a;padding:9px 12px;text-align:center;font-weight:700;width:38px;">No.</th>
                    <th style="font-size:10px;color:#e8c46a;padding:9px 12px;text-align:left;font-weight:700;">Name / പേര്</th>
                    <th style="font-size:10px;color:#e8c46a;padding:9px 12px;text-align:left;font-weight:700;">Birth Star / നക്ഷത്രം</th>
                  </tr>
                </thead>
                <tbody>${personsRows}</tbody>
              </table>

              <!-- Total -->
              <table style="width:100%;border-collapse:collapse;margin-top:18px;background:#1a0404;">
                <tr>
                  <td style="padding:13px 18px;font-size:14px;color:#e8c46a;font-weight:600;">മൊത്തം അടച്ചത് / Total Amount Paid</td>
                  <td style="padding:13px 18px;font-size:26px;font-weight:700;color:#e8c46a;text-align:right;">₹${total}</td>
                </tr>
              </table>

              <!-- Signatory -->
              <table style="width:100%;margin-top:22px;">
                <tr>
                  <td style="text-align:right;">
                    <div style="display:inline-block;width:80px;height:80px;border-radius:50%;border:2px dashed #c9973a;text-align:center;padding:18px 6px;box-sizing:border-box;">
                      <span style="font-size:7px;color:#8a6020;line-height:1.6;">അധികൃത ഒപ്പ്<br/>Authorised<br/>Signatory</span>
                    </div>
                  </td>
                </tr>
              </table>

              <!-- Footer -->
              <div style="text-align:center;margin-top:22px;padding-top:16px;border-top:1px solid #c9973a;">
                <div style="font-size:13px;font-style:italic;color:#3a0c0c;margin-bottom:4px;">"ഈശ്വരൻ നിങ്ങൾക്കും കുടുംബത്തിനും സമൃദ്ധി നൽകട്ടെ"</div>
                <div style="font-size:10px;color:#8a6020;">${t("rcptNote") || "ഈ രസീത് ഭാവിയിൽ ആവശ്യത്തിനായി സൂക്ഷിക്കുക."}</div>
                <div style="font-size:9px;color:#aaa;margin-top:3px;">${now}</div>
                <div style="font-size:8px;letter-spacing:3px;color:#ccc;margin-top:14px;">${data.booking.receipt_number}</div>
              </div>

            </div>
          </div>
        </div>
      `;
      document.body.appendChild(container);

      // Load html2pdf from CDN dynamically
      await new Promise((resolve, reject) => {
        if (window.html2pdf) { resolve(); return; }
        const s = document.createElement("script");
        s.src = "https://cdnjs.cloudflare.com/ajax/libs/html2pdf.js/0.10.1/html2pdf.bundle.min.js";
        s.onload = resolve;
        s.onerror = reject;
        document.head.appendChild(s);
      });

      // Small delay to ensure scripts and fonts are ready
      await new Promise(r => setTimeout(r, 800));

      const element = container.querySelector("div[style*='794px']");
      await window.html2pdf().set({
        margin: 0,
        filename: `Receipt_${data.booking.receipt_number}.pdf`,
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true, allowTaint: true, logging: false, backgroundColor: "#ffffff" },
        jsPDF: { unit: "px", format: [794, 1123], orientation: "portrait" },
      }).from(element).save();

      document.body.removeChild(container);
    } catch (err) {
      console.error("PDF download error", err);
    } finally {
      setDownloading(false);
    }
  };

  const handleCancelClose = () => { setShowCancelConfirm(false); onClose(); };

  if (loading || !data) return null;

  const total = (parseFloat(data.booking.category_amount) * data.booking.total_persons).toFixed(2);
  const bookingDate = new Date(data.booking.booking_date).toLocaleDateString("en-IN", { day:"2-digit", month:"long", year:"numeric" });

  return createPortal(
    <div className="adv-receipt-overlay" onClick={showCancelConfirm ? () => {} : () => setShowCancelConfirm(true)}>
      <div className="adv-receipt-modal" onClick={e => e.stopPropagation()}>

        {/* ── Decorative top bar ── */}
        <div className="adv-receipt-topbar" />

        {/* ── Header ── */}
        <div className="adv-receipt-header">
          <div className="adv-receipt-logo-wrap">
            <div className="adv-receipt-logo-ring" />
            <img src="./logo.png" alt="Temple" className="adv-receipt-logo-img"
              onError={e => { e.target.style.display="none"; e.target.nextSibling.style.display="flex"; }} />
            <div className="adv-receipt-logo-fallback">ॐ</div>
          </div>
          <div className="adv-receipt-temple-info">
            <h2>കൊടുങ്ങല്ലൂർ കാവിൽ ശ്രീ ഭദ്രകാളി ദേവി ക്ഷേത്രം</h2>
            <p className="adv-receipt-addr-ml">പാണ്ടനാട് · ചെങ്ങന്നൂർ</p>
            <p className="adv-receipt-name-en">Kodungallur Kavil Sree Bhadrakali Devi Temple</p>
            <p className="adv-receipt-addr-en">Pandanad, Chengannur</p>
          </div>
          <div className="adv-receipt-badge">✦ രസീത് ✦</div>
        </div>

        {/* ── Ornament rule ── */}
        <div className="adv-receipt-rule"><span className="adv-receipt-rule-dot">❖</span></div>

        {/* ── Body ── */}
        <div className="adv-receipt-body">
          <div className="adv-receipt-info-grid">
            <div className="adv-receipt-info-cell">
              <span className="adv-ri-lbl">രസീത് നമ്പർ / {t("rcptNo")}</span>
              <span className="adv-ri-val adv-ri-highlight">{data.booking.receipt_number}</span>
            </div>
            <div className="adv-receipt-info-cell">
              <span className="adv-ri-lbl">തീയതി / {t("rcptDate")}</span>
              <span className="adv-ri-val">{bookingDate}</span>
            </div>
            <div className="adv-receipt-info-cell">
              <span className="adv-ri-lbl">മൊബൈൽ / {t("rcptMobile")}</span>
              <span className="adv-ri-val">+91 {data.booking.mobile_number}</span>
            </div>
            <div className="adv-receipt-info-cell">
              <span className="adv-ri-lbl">ആൾക്കാർ / Persons</span>
              <span className="adv-ri-val">{data.booking.total_persons}</span>
            </div>
            <div className="adv-receipt-info-cell">
              <span className="adv-ri-lbl">ഒരാൾക്ക് / Rate</span>
              <span className="adv-ri-val">₹{parseFloat(data.booking.category_amount).toFixed(2)}</span>
            </div>
            <div className="adv-receipt-info-cell">
              <span className="adv-ri-lbl">പണമടക്കൽ / Payment</span>
              <span className="adv-ri-val">പണം (Cash)</span>
            </div>
          </div>

          {/* ── Category highlighted ── */}
          <div className="adv-receipt-category-highlight">
            <span className="adv-rch-label">⭐ വഴിപാട് / {t("rcptCategory")}</span>
            <span className="adv-rch-value">✦ {data.booking.category_name} ✦</span>
          </div>

          <div className="adv-receipt-divider" />

          {/* ── Devotee table ── */}
          <div className="adv-receipt-section-title">
            <span className="adv-rst-line" />
            <span className="adv-rst-text">ഭക്തജന വിവരങ്ങൾ / {t("bkgSecDevotee")}</span>
            <span className="adv-rst-line" />
          </div>
          <table className="adv-receipt-table">
            <thead>
              <tr>
                <th>നം.</th>
                <th>പേര് / Name</th>
                <th>നക്ഷത്രം / Nakshathram</th>
              </tr>
            </thead>
            <tbody>
              {data.persons.map((p, i) => (
                <tr key={i}>
                  <td className="adv-rt-no">{i + 1}</td>
                  <td className="adv-rt-name">{p.name}</td>
                  <td className="adv-rt-star">{p.nakshathram || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* ── Total ── */}
          <div className="adv-receipt-total-box">
            <span className="adv-rtb-label">മൊത്തം / {t("rcptTotalPaid")}</span>
            <span className="adv-rtb-amount">₹{total}</span>
          </div>
        </div>

        {/* ── Footer actions ── */}
        <div className="adv-receipt-footer">
          <div className="adv-receipt-actions">
            <button className="adv-rcpt-btn adv-rcpt-btn-print" onClick={handlePrint}>
              <FaPrint /> {t("btnPrint")}
            </button>
            <button
              className={`adv-rcpt-btn adv-rcpt-btn-pdf ${downloading ? "loading" : ""}`}
              onClick={handleDownloadPDF}
              disabled={downloading}
            >
              {downloading
                ? <><span className="adv-rcpt-spin" /> Downloading…</>
                : <><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg> Download PDF</>
              }
            </button>
            <button className="adv-rcpt-btn adv-rcpt-btn-close" onClick={() => setShowCancelConfirm(true)}>
              <FaClose /> {t("btnClose")}
            </button>
          </div>
          <p className="adv-receipt-note">{t("rcptNote")}</p>
        </div>

        {/* ── Cancel confirm overlay ── */}
        {showCancelConfirm && (
          <div className="adv-cancel-confirm-overlay">
            <div className="adv-confirm-box">
              <div className="adv-confirm-icon">⚠</div>
              <h3>{t("rcptConfirmTitle") || "Are you sure?"}</h3>
              <p>{t("rcptConfirmMsg") || "Do you want to close this receipt? Make sure you have downloaded or printed it."}</p>
              <div className="adv-confirm-box-actions">
                <button className="adv-btn adv-btn-ghost" onClick={() => setShowCancelConfirm(false)}>
                  {t("rcptGoBack") || "Go Back"}
                </button>
                <button className="adv-btn adv-btn-danger" onClick={handleCancelClose}>
                  {t("rcptYesClose") || "Yes, Close"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}

/* ═══════════════════════════════════════════════════
   LOADING SPINNER  (same as Adevotees OmLoader)
═══════════════════════════════════════════════════ */
function OmLoader() {
  const { t } = useTranslation();
  return (
    <div className="adv-loader-wrap">
      <div className="adv-loader-rings">
        {[90, 70, 52].map((s, i) => (
          <div key={i} className="adv-loader-ring" style={{ width: s, height: s, animationDelay: `${i * 0.3}s` }} />
        ))}
        <div className="adv-loader-om">ॐ</div>
      </div>
      <p className="adv-loader-text">{t("bkgLoading")}</p>
    </div>
  );
}

/* ═══════════════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════════════ */
export default function AOfferings() {
  const { t } = useTranslation();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formModal, setFormModal] = useState(null);
  const [receiptModal, setReceiptModal] = useState(null);
  const [toast, setToast] = useState(null);
  const [logoFace, setLogoFace] = useState("om");
  const [cardFace, setCardFace] = useState("om");

  const API = process.env.REACT_APP_API_URL || "http://localhost:5000";

  const showToast = (message, type = "info") => setToast({ message, type });

  useEffect(() => {
    const timer = setInterval(() => {
      setLogoFace(f => f === "om" ? "logo" : "om");
      setCardFace(f => f === "om" ? "logo" : "om");
    }, 3000);
    return () => clearInterval(timer);
  }, []);

  const fetchCategories = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/vazhipadu-categories`);
      if (!res.ok) throw new Error("Failed to fetch");
      const data = await res.json();
      setCategories(data.filter(c => c.active));
    } catch (err) {
      showToast(t("bkgErrFetch"), "error");
    } finally {
      setLoading(false);
    }
  }, [API]);

  useEffect(() => { fetchCategories(); }, [fetchCategories]);

  const handleBookSuccess = (booking) => {
    setFormModal(null);
    setReceiptModal(booking.booking_id);
  };

  return (
    <>
      <OfferingsStyles />

      {/* ── HERO (identical structure to Adevotees) ── */}
      <div className="adv-hero">
        <div className="adv-hero-bg" />
        <div className="adv-hero-rings">
          {[220, 170, 125, 85].map((s, i) => (
            <div key={i} className="adv-hero-ring" style={{ width: s, height: s, animationDelay: `${i * 0.5}s` }} />
          ))}
        </div>
        <div className="adv-hero-medallion">
          <div className="adv-hero-med-ring" />
          <div className="adv-hero-med-ring adv-hero-med-ring-2" />
          <div className={`adv-hero-face ${logoFace === "om" ? "visible" : ""}`}>
            <span className="adv-hero-om">ॐ</span>
          </div>
          <div className={`adv-hero-face ${logoFace === "logo" ? "visible" : ""}`}>
            <img src="./logo.png" alt="Temple" className="adv-hero-logo-img" />
          </div>
          <div className="adv-hero-med-halo" />
        </div>
        <div className="adv-hero-text">
          <div className="adv-hero-eyebrow">✦ {t("templeName")} ✦</div>
          <h1 className="adv-hero-title">{t("bookingPageTitle") || "Vazhipadu Bookings"}</h1>
          <p className="adv-hero-sub">{t("bookingPageSub") || "Book sacred offerings for the devotees"}</p>
        </div>
        <div className="adv-hero-rule" />

        {/* Stat row — offerings count */}
        <div className="adv-stat-row">
          <div className="adv-stat-card" style={{ "--scolor": "#c9973a" }}>
            <div className="adv-stat-val">{categories.length}</div>
            <div className="adv-stat-label">{t("bkgStatOfferings")}</div>
          </div>
          <div className="adv-stat-card" style={{ "--scolor": "#2a6b2a" }}>
            <div className="adv-stat-val">{categories.length > 0 ? t("bkgStatActive") : "—"}</div>
            <div className="adv-stat-label">{t("bkgStatStatus")}</div>
          </div>
        </div>
      </div>

      {/* ── TOOLBAR ── */}
      <div className="adv-toolbar">
        <div className="adv-toolbar-title">
          <FaShoppingBag className="adv-toolbar-title-icon" />
          <span>{t("bkgToolbarHint")}</span>
        </div>
        <div className="adv-toolbar-btns">
          <button className="adv-action-btn adv-btn-refresh" onClick={fetchCategories}>
            <FaSync className={loading ? "spin" : ""} /> {t("btnRefresh")}
          </button>
        </div>
      </div>

      {/* ── RESULT INFO ── */}
      <div className="adv-result-info">
        {!loading && (
          <span>
            {t("bkgShowing")} <strong>{categories.length}</strong> {t("bkgActive")}{" "}
            {categories.length === 1 ? t("bkgOfferingOne") : t("bkgOfferingMany")}
          </span>
        )}
      </div>

      {/* ── OFFERINGS GRID (styled like vazhipadu grid from Index.js) ── */}
      <div className="adv-offerings-wrap">
        {loading ? (
          <OmLoader />
        ) : categories.length === 0 ? (
          <div className="adv-empty">
            <div className="adv-empty-om">ॐ</div>
            <h3 className="adv-empty-title">{t("bkgEmptyTitle")}</h3>
            <p className="adv-empty-sub">{t("bkgEmptySub")}</p>
            <button className="adv-btn adv-btn-ghost" onClick={fetchCategories}>{t("btnRefresh")}</button>
          </div>
        ) : (
          <div className="adv-vazhipadu-grid">
            {categories.map((cat, i) => (
              <div
                key={cat.vazhipadu_id}
                className="adv-vazhipadu-card"
                style={{ animationDelay: `${i * 0.07}s` }}
                onClick={() => setFormModal(cat)}
              >
                {/* Top accent bar */}
                <div className="adv-vz-topbar" />

                {/* Icon medallion */}
                <div className="adv-vz-medallion">
                  <div className="adv-vz-med-ring" />
                  <div className={`adv-hero-face ${cardFace === "om" ? "visible" : ""}`}>
                    <span className="adv-vz-om">ॐ</span>
                  </div>
                  <div className={`adv-hero-face ${cardFace === "logo" ? "visible" : ""}`}>
                    <img src="./logo.png" alt="Temple" className="adv-vz-logo-img"
                      onError={e => { e.target.style.display="none"; e.target.nextSibling.style.display="flex"; }} />
                    <span style={{display:"none"}} className="adv-vz-om">ॐ</span>
                  </div>
                </div>

                <div className="adv-vz-name">{cat.name}</div>

                {cat.description && (
                  <p className="adv-vz-desc">{cat.description}</p>
                )}

                <div className="adv-vz-footer">
                  <div className="adv-vz-price">
                    <FaRupeeSign className="adv-vz-rupee" />
                    <span>{parseFloat(cat.amount).toFixed(2)}</span>
                    <span className="adv-vz-per">/ {t("bkgPerPerson")}</span>
                  </div>
                  <button className="adv-vz-book-btn">
                    <FaCalendarAlt /> {t("bkgBtnBookNow")}
                  </button>
                </div>

                {/* Corner ornaments */}
                <span className="adv-vz-corner vz-tl" />
                <span className="adv-vz-corner vz-br" />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── MODALS ── */}
      {formModal && (
        <BookingFormModal
          category={formModal}
          onClose={() => setFormModal(null)}
          onBooked={handleBookSuccess}
          showToast={showToast}
        />
      )}
      {receiptModal && (
        <ReceiptModal
          bookingId={receiptModal}
          onClose={() => setReceiptModal(null)}
        />
      )}
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </>
  );
}

/* ═══════════════════════════════════════════════════
   STYLES
═══════════════════════════════════════════════════ */
function OfferingsStyles() {
  return (
    <style>{`
      @import url('https://fonts.googleapis.com/css2?family=Cinzel+Decorative:wght@400;700;900&family=Cinzel:wght@400;500;600;700&family=EB+Garamond:ital,wght@0,400;0,500;0,600;1,400;1,500&display=swap');

      :root {
        --gold-bright: #e8c46a; --gold-mid: #c9973a; --gold-dim: #8a6020;
        --crimson-deep: #1a0404; --crimson-mid: #2c0808; --crimson-rich: #3d0c0c;
        --hero-1: #2e0c0c; --hero-2: #1c0505; --hero-3: #100202;
        --parchment: #FDF3E3; --parchment-dark: #f5e8cc;
        --text-dark: #1a0404; --text-mid: #4a1a08;
      }

      /* ══ HERO ══ */
      .adv-hero { position: relative; overflow: hidden; background: linear-gradient(160deg, var(--hero-1) 0%, var(--hero-2) 50%, var(--hero-3) 100%); padding: 40px 32px 0; display: flex; flex-direction: column; align-items: center; border-radius: 16px 16px 0 0; }
      .adv-hero-bg { position: absolute; inset: 0; pointer-events: none; background: radial-gradient(ellipse at 15% 30%, rgba(160,35,35,0.35) 0%, transparent 50%), radial-gradient(ellipse at 85% 70%, rgba(80,15,15,0.3) 0%, transparent 45%); }
      .adv-hero-rings { position: absolute; top: 30px; right: 60px; width: 230px; height: 230px; pointer-events: none; }
      .adv-hero-ring { position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); border-radius: 50%; border: 1px solid rgba(232,196,106,0.07); animation: heroRingPulse 4s ease-in-out infinite alternate; }
      .adv-hero-ring:nth-child(odd) { border-style: dashed; }
      @keyframes heroRingPulse { from { opacity: 0.3; } to { opacity: 0.8; } }
      .adv-hero-medallion { width: 90px; height: 90px; border-radius: 50%; background: radial-gradient(circle at 40% 35%, #5a1515 0%, #2a0707 55%, #0e0101 100%); border: 1px solid rgba(232,196,106,0.3); display: flex; align-items: center; justify-content: center; position: relative; margin-bottom: 20px; box-shadow: 0 0 30px rgba(232,196,106,0.15), inset 0 1px 0 rgba(255,248,231,0.06); }
      .adv-hero-med-ring { position: absolute; inset: -5px; border-radius: 50%; border: 1px dashed rgba(232,196,106,0.25); animation: slowSpin 10s linear infinite; }
      .adv-hero-med-ring-2 { inset: -10px; border-style: dotted; border-color: rgba(232,196,106,0.12); animation-duration: 18s; animation-direction: reverse; }
      @keyframes slowSpin { to { transform: rotate(360deg); } }
      .adv-hero-face { position: absolute; inset: 0; border-radius: 50%; display: flex; align-items: center; justify-content: center; opacity: 0; transform: scale(0.7) rotateY(90deg); transition: opacity 0.55s ease, transform 0.55s ease; }
      .adv-hero-face.visible { opacity: 1; transform: scale(1) rotateY(0deg); }
      .adv-hero-om { font-family: 'Cinzel Decorative', cursive; font-size: 34px; background: linear-gradient(180deg, #fff8e7, var(--gold-bright), var(--gold-mid)); -webkit-background-clip: text; -webkit-text-fill-color: transparent; filter: drop-shadow(0 0 8px rgba(232,196,106,0.5)); }
      .adv-hero-logo-img { width: 54px; height: 54px; object-fit: contain; filter: drop-shadow(0 0 8px rgba(232,196,106,0.4)); }
      .adv-hero-med-halo { position: absolute; inset: -16px; border-radius: 50%; background: radial-gradient(circle, rgba(232,196,106,0.08) 0%, transparent 65%); pointer-events: none; }
      .adv-hero-text { text-align: center; position: relative; z-index: 1; }
      .adv-hero-eyebrow { font-family: 'Cinzel', serif; font-size: 8.5px; letter-spacing: 2px; color: rgba(232,196,106,0.35); text-transform: uppercase; margin-bottom: 10px; }
      .adv-hero-title { font-family: 'Cinzel Decorative', cursive; font-size: 28px; font-weight: 900; background: linear-gradient(135deg, #fffaf0, var(--gold-bright), var(--gold-mid)); -webkit-background-clip: text; -webkit-text-fill-color: transparent; line-height: 1.2; margin-bottom: 8px; }
      .adv-hero-sub { font-family: 'EB Garamond', serif; font-style: italic; font-size: 15px; color: rgba(245,228,176,0.4); margin-bottom: 20px; }
      .adv-hero-rule { width: 60%; max-width: 400px; height: 1px; margin: 0 auto 24px; background: linear-gradient(90deg, transparent, rgba(201,151,58,0.4), transparent); }
      .adv-stat-row { display: flex; gap: 14px; flex-wrap: wrap; justify-content: center; padding: 0 0 28px; position: relative; z-index: 1; }
      .adv-stat-card { background: rgba(255,255,255,0.04); border: 1px solid rgba(201,151,58,0.18); padding: 14px 22px; text-align: center; cursor: pointer; clip-path: polygon(10px 0%, 100% 0%, calc(100% - 10px) 100%, 0% 100%); transition: background 0.2s, border-color 0.2s; min-width: 90px; }
      .adv-stat-card:hover { background: rgba(201,151,58,0.08); border-color: rgba(201,151,58,0.35); }
      .adv-stat-val { font-family: 'Cinzel Decorative', cursive; font-size: 22px; font-weight: 900; color: var(--scolor, var(--gold-bright)); }
      .adv-stat-label { font-family: 'Cinzel', serif; font-size: 8px; letter-spacing: 1.5px; text-transform: uppercase; color: rgba(245,228,176,0.4); margin-top: 3px; }

      /* ══ TOOLBAR ══ */
      .adv-toolbar { display: flex; flex-wrap: wrap; gap: 12px; align-items: center; padding: 20px 28px 12px; background: var(--parchment); border-bottom: 1px solid rgba(201,151,58,0.15); position: sticky; top: 0; z-index: 10; box-shadow: 0 4px 12px rgba(26,4,4,0.06); }
      .adv-toolbar-title { display: flex; align-items: center; gap: 10px; font-family: 'EB Garamond', serif; font-style: italic; font-size: 15px; color: rgba(74,26,8,0.6); flex: 1; }
      .adv-toolbar-title-icon { color: var(--gold-dim); font-size: 14px; }
      .adv-toolbar-btns { display: flex; gap: 8px; margin-left: auto; }
      .adv-action-btn { display: flex; align-items: center; gap: 7px; font-family: 'Cinzel', serif; font-size: 9.5px; font-weight: 700; letter-spacing: 1.5px; text-transform: uppercase; border: none; padding: 10px 18px; cursor: pointer; clip-path: polygon(10px 0%, calc(100% - 10px) 0%, 100% 50%, calc(100% - 10px) 100%, 10px 100%, 0% 50%); transition: all 0.25s; }
      .adv-btn-refresh { background: rgba(201,151,58,0.1); border: 1px solid rgba(201,151,58,0.3); color: var(--gold-dim); }
      .adv-btn-refresh:hover { background: rgba(201,151,58,0.18); }
      .spin { animation: spinIcon 0.8s linear infinite; }
      @keyframes spinIcon { to { transform: rotate(360deg); } }

      .adv-result-info { padding: 6px 28px; font-family: 'EB Garamond', serif; font-style: italic; font-size: 13px; color: rgba(74,26,8,0.45); background: var(--parchment); }

      /* ══ OFFERINGS GRID ══ */
      .adv-offerings-wrap { padding: 28px 28px 48px; }

      .adv-vazhipadu-grid {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: 20px;
        max-width: 1100px;
        margin: 0 auto;
      }

      .adv-vazhipadu-card {
        position: relative;
        padding: 28px 22px;
        border: 1px solid rgba(232,196,106,0.1);
        background: rgba(232,196,106,0.025);
        background: #0e0101; 
        overflow: hidden;
        clip-path: polygon(0 0, calc(100% - 18px) 0, 100% 18px, 100% 100%, 18px 100%, 0 calc(100% - 18px));
        cursor: pointer;
        transition: all 0.35s ease;
        animation: rowIn 0.4s ease both;
        display: flex;
        flex-direction: column;
        gap: 8px;
      }
      @keyframes rowIn { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }

      .adv-vazhipadu-card::before {
        content: '';
        position: absolute;
        top: 0; left: 0;
        width: 3px; height: 0;
        background: linear-gradient(180deg, #e8c46a, #c9973a);
        transition: height 0.35s ease;
      }
      .adv-vazhipadu-card:hover::before { height: 100%; }
      .adv-vazhipadu-card:hover {
        border-color: rgba(232,196,106,0.22);
        background: rgba(232,196,106,0.05);
        transform: translateY(-3px);
        box-shadow: 0 14px 36px rgba(0,0,0,0.4);
        background: #0e0101; 
      }

      .adv-vz-topbar { display: none; }

      .adv-vz-medallion {
        width: 54px; height: 54px; border-radius: 50%;
        background: radial-gradient(circle at 40% 35%, #5a1515 0%, #2a0707 55%, #0e0101 100%);
        border: 1px solid rgba(232,196,106,0.35);
        display: flex; align-items: center; justify-content: center;
        position: relative;
        box-shadow: 0 0 20px rgba(232,196,106,0.1);
        flex-shrink: 0;
        margin-bottom: 4px;
        align-self: center;
      }
      .adv-vz-med-ring {
        position: absolute; inset: -4px; border-radius: 50%;
        border: 1px dashed rgba(232,196,106,0.25);
        animation: slowSpin 12s linear infinite;
      }
      .adv-vz-om {
        font-family: 'Cinzel Decorative', cursive;
        font-size: 18px;
        background: linear-gradient(180deg, #fff8e7, #e8c46a, #c9973a);
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
        filter: drop-shadow(0 0 6px rgba(232,196,106,0.4));
        position: relative; z-index: 1;
        
      }
      .adv-vz-logo-img { width: 34px; height: 34px; object-fit: contain; filter: drop-shadow(0 0 6px rgba(232,196,106,0.4)); position: relative; z-index: 1; }

      .adv-vz-name {
        font-family: 'Cinzel', serif;
        font-size: 25px;
        font-weight: bold;
        letter-spacing: 1.5px;
        text-transform: uppercase;
        background: linear-gradient(135deg, #fffaf0, #e8c46a);
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
        margin-bottom: 4px;
        text-align:center;
      }

      .adv-vz-desc {
        font-family: 'EB Garamond', serif;
        font-size: 18px;
        color: rgba(245,228,176,0.5);
        line-height: 1.6;
        flex: 1;
        margin: 0;
        text-align:center;

      }

      .adv-vz-footer {
        width: 100%;
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-top: 8px;
        padding-top: 14px;
        border-top: 1px solid rgba(232,196,106,0.1);
        gap: 8px;
        flex-wrap: wrap;
      }

      .adv-vz-price {
        display: flex;
        align-items: baseline;
        gap: 3px;
        font-family: 'Cinzel Decorative', cursive;
        font-size: 18px;
        font-weight: bold;
        color: #c9973a;
      }
      .adv-vz-rupee { font-size: 12px; color: #c9973a; }
      .adv-vz-per { font-family: 'EB Garamond', serif; font-style: italic; font-size: 11px; color: rgba(245,228,176,0.35); font-weight: 400; }

      .adv-vz-book-btn {
        display: inline-flex; align-items: center; gap: 6px;
        font-family: 'Cinzel', serif;
        font-size: 9px;
        font-weight: 700;
        letter-spacing: 1.5px;
        text-transform: uppercase;
        color: #1a0404;
        background: linear-gradient(135deg, #c9973a, #e8c46a, #c9973a);
        border: none;
        padding: 7px 16px;
        cursor: pointer;
        clip-path: polygon(8px 0%, calc(100% - 8px) 0%, 100% 50%, calc(100% - 8px) 100%, 8px 100%, 0% 50%);
        transition: all 0.3s ease;
        position: relative;
        overflow: hidden;
      }
      .adv-vz-book-btn::after {
        content: '';
        position: absolute;
        top: -50%; left: -70%;
        width: 40%; height: 200%;
        background: rgba(255,255,255,0.22);
        transform: skewX(-18deg);
        transition: left 0.5s ease;
        pointer-events: none;
      }
      .adv-vazhipadu-card:hover .adv-vz-book-btn::after { left: 130%; }
      .adv-vz-book-btn:hover { filter: brightness(1.1); transform: scale(1.05); }

      /* Corner ornaments */
      .adv-vz-corner { position: absolute; width: 12px; height: 12px; border-color: rgba(201,151,58,0.35); border-style: solid; }
      .adv-vz-corner.vz-tl { top: 8px; left: 8px; border-width: 1.5px 0 0 1.5px; }
      .adv-vz-corner.vz-br { bottom: 8px; right: 8px; border-width: 0 1.5px 1.5px 0; }

      /* ══ EMPTY ══ */
      .adv-empty { display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 70px 0; gap: 14px; text-align: center; }
      .adv-empty-om { font-family: 'Cinzel Decorative', cursive; font-size: 52px; background: linear-gradient(180deg, #e8c46a, #c9973a); -webkit-background-clip: text; -webkit-text-fill-color: transparent; opacity: 0.25; }
      .adv-empty-title { font-family: 'Cinzel Decorative', cursive; font-size: 20px; color: rgba(245,228,176,0.6); }
      .adv-empty-sub { font-family: 'EB Garamond', serif; font-style: italic; font-size: 15px; color: rgba(245,228,176,0.35); max-width: 320px; }

      /* ══ LOADER ══ */
      .adv-loader-wrap { display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 80px 0; gap: 14px; }
      .adv-loader-rings { position: relative; width: 100px; height: 100px; margin-bottom: 10px; }
      .adv-loader-ring { position: absolute; top: 50%; left: 50%; transform: translate(-50%,-50%); border-radius: 50%; border: 1px dashed rgba(201,151,58,0.3); animation: loaderPulse 2s ease-in-out infinite; }
      .adv-loader-ring:nth-child(2) { border-style: solid; border-color: rgba(201,151,58,0.15); }
      .adv-loader-ring:nth-child(3) { animation: slowSpin 3s linear infinite; border-color: rgba(201,151,58,0.25); }
      @keyframes loaderPulse { 0%,100% { opacity: 0.3; transform: translate(-50%,-50%) scale(0.95); } 50% { opacity: 1; transform: translate(-50%,-50%) scale(1.02); } }
      .adv-loader-om { position: absolute; top: 50%; left: 50%; transform: translate(-50%,-50%); font-family: 'Cinzel Decorative', cursive; font-size: 28px; background: linear-gradient(180deg, #fff8e7, var(--gold-bright), var(--gold-mid)); -webkit-background-clip: text; -webkit-text-fill-color: transparent; animation: omGlow 2s ease-in-out infinite alternate; }
      @keyframes omGlow { from { filter: drop-shadow(0 0 4px rgba(232,196,106,0.3)); } to { filter: drop-shadow(0 0 12px rgba(232,196,106,0.7)); } }
      .adv-loader-text { font-family: 'EB Garamond', serif; font-style: italic; font-size: 15px; color: rgba(74,26,8,0.4); }

      /* ══ BUTTONS ══ */
      .adv-btn { display: inline-flex; align-items: center; gap: 7px; font-family: 'Cinzel', serif; font-size: 9.5px; font-weight: 700; letter-spacing: 1.5px; text-transform: uppercase; border: none; padding: 10px 18px; cursor: pointer; clip-path: polygon(10px 0%,calc(100% - 10px) 0%,100% 50%,calc(100% - 10px) 100%,10px 100%,0 50%); transition: all 0.2s; border: 1px solid transparent; }
      .adv-btn:disabled { opacity: 0.38; cursor: not-allowed; }
      .adv-btn-ghost { background: none; font-weight: bold; border-color: rgba(149,27,27,0.5); color: rgb(74,26,8); }
      .adv-btn-ghost:hover { background: rgba(201,151,58,0.06); }
      .adv-btn-danger { background: linear-gradient(135deg,#5a0000,#8b1a1a); color: #fff; border-color: rgba(139,26,26,0.4); }

      /* ══ MODAL OVERLAY ══ */
      .adv-modal-overlay { position: fixed; inset: 0; z-index: 1000; background: rgba(8,1,1,0.85); backdrop-filter: blur(7px); display: flex; align-items: center; justify-content: center; padding: 20px; animation: overlayIn 0.2s ease; }
      @keyframes overlayIn { from { opacity: 0; } to { opacity: 1; } }

      /* ══ REGAL MODAL BASE ══ */
      .adv-regal-modal { position: relative; width: 100%; background: linear-gradient(160deg, #FDF3E3 0%, #faf0dc 55%, #f5e8cc 100%); border: 1px solid rgba(201,151,58,0.35); box-shadow: 0 36px 110px rgba(26,4,4,0.7), 0 0 0 1px rgba(201,151,58,0.06) inset; animation: regalIn 0.35s cubic-bezier(0.23,1,0.32,1) both; overflow: hidden; }
      @keyframes regalIn { from { opacity: 0; transform: scale(0.93) translateY(26px); } to { opacity: 1; transform: scale(1) translateY(0); } }
      .adv-edit-regal { max-width: 580px; max-height: 92vh; overflow-y: auto; }
      .adv-edit-regal::-webkit-scrollbar { width: 6px; }
      .adv-edit-regal::-webkit-scrollbar-track { background: linear-gradient(180deg,#f5e8cc,#faf0dc); border-radius: 10px; }
      .adv-edit-regal::-webkit-scrollbar-thumb { background: linear-gradient(180deg, var(--hero-1), #5a1515, var(--gold-mid)); border-radius: 10px; border: 1px solid rgba(232,196,106,0.3); }

      .adv-rm-topglow { position: absolute; top: 0; left: 0; right: 0; height: 3px; background: linear-gradient(90deg, transparent 0%, rgba(201,151,58,0.2) 20%, rgba(232,196,106,0.9) 50%, rgba(201,151,58,0.2) 80%, transparent 100%); z-index: 10; }
      .adv-cm-corner { position: absolute; width: 16px; height: 16px; border-color: rgba(201,151,58,0.5); border-style: solid; z-index: 10; }
      .adv-cm-corner.tl { top: 14px; left: 14px; border-width: 1.5px 0 0 1.5px; }
      .adv-cm-corner.tr { top: 14px; right: 14px; border-width: 1.5px 1.5px 0 0; }
      .adv-cm-corner.bl { bottom: 14px; left: 14px; border-width: 0 0 1.5px 1.5px; }
      .adv-cm-corner.br { bottom: 14px; right: 14px; border-width: 0 1.5px 1.5px 0; }

      .adv-rm-header { position: relative; overflow: hidden; background: linear-gradient(160deg, var(--hero-1), var(--hero-2) 55%, var(--hero-3)); padding: 38px 28px 28px; text-align: center; border-bottom: 1px solid rgba(201,151,58,0.2); }
      .adv-rm-header-bg { position: absolute; inset: 0; background: radial-gradient(ellipse at 50% 40%, rgba(150,30,30,0.28) 0%, transparent 65%); pointer-events: none; }
      .adv-rm-header-inner { position: relative; z-index: 1; display: flex; flex-direction: column; align-items: center; gap: 8px; }
      .adv-rm-close { position: absolute; top: 16px; right: 16px; z-index: 5; background: rgba(255,255,255,0.06); border: 1px solid rgba(232,196,106,0.2); color: rgba(232,196,106,0.55); border-radius: 50%; width: 30px; height: 30px; display: flex; align-items: center; justify-content: center; cursor: pointer; font-size: 11px; transition: all 0.2s; }
      .adv-rm-close:hover { background: rgba(255,255,255,0.12); color: var(--gold-bright); transform: rotate(90deg); }
      .adv-rm-header-icon-wrap { position: relative; width: 72px; height: 72px; border-radius: 50%; display: flex; align-items: center; justify-content: center; background: rgba(232,196,106,0.08); border: 1px solid rgba(232,196,106,0.2); }
      .adv-rm-hicon-ring { position: absolute; inset: -5px; border-radius: 50%; border: 1px dashed rgba(232,196,106,0.28); animation: slowSpin 9s linear infinite; }
      .adv-rm-hicon { font-size: 28px; color: var(--gold-bright); filter: drop-shadow(0 0 10px rgba(232,196,106,0.4)); position: relative; z-index: 1; }
      .adv-rm-header-eyebrow { font-family: 'Cinzel', serif; font-size: 7.5px; letter-spacing: 3px; color: rgba(232,196,106,0.3); text-transform: uppercase; }
      .adv-rm-title { font-family: 'Cinzel Decorative', cursive; font-size: 22px; font-weight: 900; background: linear-gradient(135deg, #fffaf0, var(--gold-bright), var(--gold-mid)); -webkit-background-clip: text; -webkit-text-fill-color: transparent; line-height: 1.25; }
      .adv-rm-name-tag { font-family: 'Cinzel', serif; font-size: 11px; letter-spacing: 1px; color: rgba(245,228,176,0.4); }
      .adv-rm-rule { display: flex; align-items: center; justify-content: center; height: 1px; margin: 0 40px; background: linear-gradient(90deg, transparent, rgba(201,151,58,0.4), transparent); position: relative; z-index: 1; }
      .adv-rm-rule-ornament { position: absolute; font-size: 12px; color: var(--gold-mid); background: var(--parchment); padding: 0 8px; line-height: 1; }

      /* ══ EDIT BODY ══ */
      .adv-edit-body { padding: 22px 28px 28px; position: relative; z-index: 1; }
      .adv-edit-section-label { font-family: 'Cinzel', serif; font-size: 8px; font-weight: 700; letter-spacing: 2.5px; text-transform: uppercase; color: var(--gold-dim); margin-bottom: 18px; padding-bottom: 7px; border-bottom: 1px solid rgba(201,151,58,0.15); }
      .adv-edit-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; margin-bottom: 14px; }
      .adv-edit-grid .full-width { grid-column: span 2; }
      .adv-edit-field { display: flex; flex-direction: column; gap: 5px; }
      .adv-edit-label { font-family: 'Cinzel', serif; font-size: 8.5px; font-weight: 700; letter-spacing: 2px; text-transform: uppercase; color: var(--gold-dim); }
      .adv-req { color: #b52020; font-weight: 900; margin-left: 2px; }
      .adv-edit-wrap { display: flex; align-items: center; background: #fff9f0; border: 1px solid rgba(201,151,58,0.28); clip-path: polygon(10px 0%, calc(100% - 10px) 0%, 100% 50%, calc(100% - 10px) 100%, 10px 100%, 0% 50%); overflow: hidden; transition: border-color 0.3s, box-shadow 0.3s; }
      .adv-edit-wrap:focus-within { border-color: var(--gold-mid); background: #fffaf0; box-shadow: 0 0 0 3px rgba(201,151,58,0.12); }
      .adv-edit-wrap.err { border-color: #b52020; background: rgba(181,32,32,0.02); }
      .adv-edit-wrap-readonly { background: #f0ece4; cursor: default; }
      .adv-edit-wrap-readonly:focus-within { box-shadow: none; border-color: rgba(201,151,58,0.28); }
      .adv-edit-prefix { font-family: 'Cinzel', serif; font-size: 11px; font-weight: 600; color: var(--text-mid); padding: 13px 12px 13px 20px; border-right: 1px solid rgba(201,151,58,0.2); flex-shrink: 0; }
      .adv-edit-sep { width: 1px; height: 20px; background: rgba(201,151,58,0.2); flex-shrink: 0; }
      .adv-edit-input { flex: 1; background: transparent; border: none; outline: none; font-family: 'EB Garamond', serif; font-size: 15px; color: var(--text-dark); padding: 13px 14px; }
      .adv-edit-input::placeholder { color: rgba(74,26,8,0.25); font-style: italic; }
      .adv-readonly-val { flex: 1; font-family: 'Cinzel', serif; font-size: 14px; font-weight: 700; color: var(--text-dark); padding: 13px 14px; }
      .adv-edit-err { font-size: 10px; color: #b52020; font-family: 'EB Garamond', serif; font-style: italic; margin-top: 2px; }

      /* ══ PERSON BLOCK ══ */
      .adv-person-block { margin-bottom: 12px; padding: 16px; border: 1px solid rgba(201,151,58,0.18); background: rgba(201,151,58,0.03); clip-path: polygon(8px 0%, calc(100% - 8px) 0%, 100% 8px, 100% calc(100% - 8px), calc(100% - 8px) 100%, 8px 100%, 0 calc(100% - 8px), 0 8px); }
      .adv-person-block-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px; }
      .adv-person-block-title { font-family: 'Cinzel', serif; font-size: 9px; font-weight: 700; letter-spacing: 2px; text-transform: uppercase; color: var(--gold-dim); }
      .adv-person-remove { display: inline-flex; align-items: center; gap: 5px; background: none; border: 1px solid rgba(139,26,26,0.25); color: rgba(139,26,26,0.7); font-family: 'Cinzel', serif; font-size: 8px; font-weight: 700; letter-spacing: 1px; text-transform: uppercase; padding: 5px 10px; cursor: pointer; clip-path: polygon(6px 0%,calc(100% - 6px) 0%,100% 50%,calc(100% - 6px) 100%,6px 100%,0 50%); transition: all 0.2s; }
      .adv-person-remove:hover { background: rgba(139,26,26,0.07); color: #8b1a1a; border-color: rgba(139,26,26,0.45); }

      /* ── NAKSHATHRAM SELECT ── */
      .adv-select-wrap { position: relative; }
      .adv-nakshathram-select { appearance: none; -webkit-appearance: none; width: 100%; background: transparent; cursor: pointer; padding-right: 28px !important; font-family: 'Noto Serif Malayalam', 'EB Garamond', serif !important; font-size: 14px !important; color: var(--ink); }
      .adv-nakshathram-select option { font-family: 'Noto Serif Malayalam', sans-serif; font-size: 14px; color: #1a0404; background: #fffdf7; padding: 6px 10px; }
      .adv-nakshathram-select option:first-child { color: #999; font-style: italic; }
      .adv-select-arrow { position: absolute; right: 12px; top: 50%; transform: translateY(-50%); color: var(--gold-mid); font-size: 13px; pointer-events: none; line-height: 1; }

      .adv-add-person-btn { width: 100%; display: flex; align-items: center; justify-content: center; gap: 8px; font-family: 'Cinzel', serif; font-size: 9px; font-weight: 700; letter-spacing: 2px; text-transform: uppercase; background: none; border: 1px dashed rgba(201,151,58,0.35); color: var(--gold-dim); padding: 12px; cursor: pointer; transition: all 0.2s; margin-top: 4px; clip-path: polygon(8px 0%,calc(100% - 8px) 0%,100% 8px,100% calc(100% - 8px),calc(100% - 8px) 100%,8px 100%,0 calc(100% - 8px),0 8px); }
      .adv-add-person-btn:hover { background: rgba(201,151,58,0.05); border-color: rgba(201,151,58,0.55); color: var(--gold-mid); }

      /* ══ ACTIONS ══ */
      .adv-edit-actions { display: flex; gap: 10px; align-items: center; justify-content: flex-end; margin-top: 20px; padding-top: 18px; border-top: 1px solid rgba(201,151,58,0.12); }
      .adv-edit-save-btn { display: inline-flex; align-items: center; gap: 8px; font-family: 'Cinzel', serif; font-size: 10px; font-weight: 700; letter-spacing: 2px; text-transform: uppercase; color: var(--crimson-deep); background: linear-gradient(135deg, #7a4e0c 0%, var(--gold-mid) 20%, var(--gold-bright) 40%, #fff8e7 55%, var(--gold-bright) 70%, var(--gold-mid) 85%, #6b4209 100%); border: none; padding: 13px 28px; cursor: pointer; clip-path: polygon(14px 0%, calc(100% - 14px) 0%, 100% 50%, calc(100% - 14px) 100%, 14px 100%, 0% 50%); box-shadow: 0 6px 24px rgba(201,151,58,0.3), inset 0 1px 0 rgba(255,255,255,0.25); transition: all 0.35s ease; position: relative; overflow: hidden; }
      .adv-edit-save-btn::before { content: ''; position: absolute; top: -50%; left: -70%; width: 40%; height: 200%; background: rgba(255,255,255,0.25); transform: skewX(-18deg); transition: left 0.55s ease; pointer-events: none; }
      .adv-edit-save-btn:hover::before { left: 130%; }
      .adv-edit-save-btn:hover { transform: translateY(-2px); box-shadow: 0 12px 32px rgba(201,151,58,0.5); filter: brightness(1.05); }
      .adv-edit-save-btn:disabled { opacity: 0.38; cursor: not-allowed; transform: none; box-shadow: none; }
      .adv-edit-saving-spinner { width: 12px; height: 12px; border-radius: 50%; border: 2px solid rgba(26,4,4,0.2); border-top-color: var(--crimson-deep); animation: spinIcon 0.7s linear infinite; flex-shrink: 0; }

      /* ══ RECEIPT MODAL ══ */
      @media print {
        body * { visibility: hidden; }
        .adv-receipt-print-area, .adv-receipt-print-area * { visibility: visible; }
      }
      .adv-receipt-overlay { position: fixed; inset: 0; z-index: 2000; background: rgba(10,2,2,0.88); backdrop-filter: blur(6px); display: flex; align-items: center; justify-content: center; padding: 20px; }
      .adv-receipt-modal { position: relative; width: 100%; max-width: 520px; background: linear-gradient(160deg,#fffdf7,#fdf6e3,#faf0d8); color: #1a0404; overflow: hidden; animation: regalIn 0.4s ease both; box-shadow: 0 30px 60px rgba(0,0,0,0.35), 0 0 0 1px rgba(201,151,58,0.25); border: 1px solid rgba(201,151,58,0.3); }
      .adv-receipt-topbar { height: 4px; background: linear-gradient(90deg, #7a4e0c, #c9973a, #e8c46a, #fff8e7, #e8c46a, #c9973a, #7a4e0c); }
      .adv-receipt-header { text-align: center; padding: 24px 20px 16px; background: linear-gradient(180deg, rgba(201,151,58,0.06), transparent); border-bottom: 1px solid rgba(201,151,58,0.2); }
      .adv-receipt-logo-wrap { position: relative; width: 64px; height: 64px; margin: 0 auto 10px; }
      .adv-receipt-logo-ring { position: absolute; inset: -4px; border-radius: 50%; border: 1px dashed rgba(201,151,58,0.5); animation: slowSpin 10s linear infinite; }
      .adv-receipt-logo-img { width: 64px; height: 64px; border-radius: 50%; object-fit: contain; border: 2px solid rgba(201,151,58,0.4); background: #fff; }
      .adv-receipt-logo-fallback { display: none; width: 64px; height: 64px; border-radius: 50%; border: 2px solid rgba(201,151,58,0.4); background: rgba(139,26,26,0.07); align-items: center; justify-content: center; font-size: 30px; color: #8b1a1a; }
      .adv-receipt-temple-info h2 { font-family: 'Noto Serif Malayalam', 'EB Garamond', serif; font-size: 15px; font-weight: 700; color: #1a0404; margin: 0 0 2px; line-height: 1.4; }
      .adv-receipt-addr-ml { font-family: 'Noto Serif Malayalam', serif; font-size: 12px; color: #4a1a08; margin: 0 0 4px !important; font-style: normal !important; }
      .adv-receipt-name-en { font-family: 'Cinzel', serif; font-size: 9px; font-weight: 600; letter-spacing: 1px; text-transform: uppercase; color: #5a3a1a; margin: 0 0 1px !important; font-style: normal !important; }
      .adv-receipt-addr-en { font-family: 'EB Garamond', serif; font-size: 11px; color: #8a6020; font-style: italic !important; margin: 0 !important; }
      .adv-receipt-badge { display: inline-block; margin-top: 10px; background: linear-gradient(135deg,#7a4e0c,#c9973a,#e8c46a,#c9973a,#7a4e0c); color: #1a0404; font-family: 'Noto Serif Malayalam','EB Garamond', serif; font-size: 13px; font-weight: 700; padding: 5px 18px; clip-path: polygon(10px 0,calc(100% - 10px) 0,100% 50%,calc(100% - 10px) 100%,10px 100%,0 50%); }
      .adv-receipt-rule { display: flex; align-items: center; margin: 0; height: 20px; background: rgba(201,151,58,0.05); }
      .adv-receipt-rule::before, .adv-receipt-rule::after { content: ''; flex: 1; height: 1px; background: linear-gradient(90deg,transparent,rgba(201,151,58,0.5)); }
      .adv-receipt-rule::after { background: linear-gradient(90deg,rgba(201,151,58,0.5),transparent); }
      .adv-receipt-rule-dot { font-size: 10px; color: #c9973a; padding: 0 10px; }
      .adv-receipt-body { padding: 18px 28px 10px; max-height: 52vh; overflow-y: auto; }
      .adv-receipt-body::-webkit-scrollbar { width: 4px; }
      .adv-receipt-body::-webkit-scrollbar-thumb { background: rgba(201,151,58,0.3); border-radius: 4px; }
      .adv-receipt-info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px 16px; margin-bottom: 14px; }
      .adv-receipt-info-cell { border-left: 3px solid rgba(201,151,58,0.5); padding-left: 10px; }
      .adv-receipt-info-cell.full-span { grid-column: span 2; }
      .adv-ri-lbl { display: block; font-family: 'Cinzel', serif; font-size: 7.5px; letter-spacing: 2px; text-transform: uppercase; color: #8a6020; margin-bottom: 2px; }
      .adv-ri-val { display: block; font-family: 'EB Garamond', serif; font-size: 14px; font-weight: 600; color: #1a0404; }
      .adv-ri-highlight { color: #8b1a1a; font-size: 15px; }
      /* ── Category highlight ── */
      .adv-receipt-category-highlight { margin: 12px 0 0; background: linear-gradient(135deg,#7a4e0c,#b8892e,#c9973a); border-radius: 2px; padding: 10px 16px; position: relative; overflow: hidden; }
      .adv-receipt-category-highlight::before { content: ''; position: absolute; inset: 0; background: linear-gradient(135deg,transparent 30%,rgba(255,255,255,0.08) 50%,transparent 70%); }
      .adv-rch-label { display: block; font-family: 'Cinzel', serif; font-size: 7.5px; letter-spacing: 2px; text-transform: uppercase; color: rgba(255,240,180,0.85); margin-bottom: 4px; }
      .adv-rch-value { display: block; font-family: 'EB Garamond', serif; font-size: 17px; font-weight: 700; color: #fff8e0; letter-spacing: 0.5px; line-height: 1.3; }
      .adv-receipt-divider { height: 1px; background: linear-gradient(90deg,transparent,rgba(201,151,58,0.5) 20%,rgba(201,151,58,0.5) 80%,transparent); margin: 14px 0; }
      .adv-receipt-section-title { display: flex; align-items: center; gap: 8px; margin: 12px 0 8px; }
      .adv-rst-line { flex: 1; height: 1px; background: rgba(201,151,58,0.35); }
      .adv-rst-text { font-family: 'Cinzel', serif; font-size: 8.5px; letter-spacing: 3px; text-transform: uppercase; color: #8a6020; white-space: nowrap; }
      .adv-receipt-table { width: 100%; border-collapse: collapse; font-family: 'EB Garamond', serif; font-size: 14px; }
      .adv-receipt-table thead tr { background: linear-gradient(135deg, #1a0404, #3a0c0c); }
      .adv-receipt-table thead th { font-family: 'Cinzel', serif; font-size: 7.5px; letter-spacing: 2px; text-transform: uppercase; color: #e8c46a; padding: 8px 12px; text-align: left; font-weight: 700; }
      .adv-receipt-table tbody tr:nth-child(even) { background: rgba(201,151,58,0.05); }
      .adv-receipt-table tbody td { padding: 7px 12px; border-bottom: 1px solid rgba(201,151,58,0.12); }
      .adv-rt-no { color: #8a6020; font-weight: 700; font-family: 'Cinzel', serif; font-size: 12px; }
      .adv-rt-name { font-weight: 600; color: #1a0404; }
      .adv-rt-star { font-style: italic; color: #5a3a1a; font-size: 13px; }
      .adv-receipt-total-box { margin-top: 14px; background: linear-gradient(135deg,#1a0404,#3a0c0c); padding: 12px 16px; display: flex; justify-content: space-between; align-items: center; }
      .adv-rtb-label { font-family: 'Cinzel', serif; font-size: 10px; letter-spacing: 2px; text-transform: uppercase; color: #e8c46a; }
      .adv-rtb-amount { font-family: 'Cinzel', serif; font-size: 22px; font-weight: 700; color: #e8c46a; }
      .adv-receipt-footer { padding: 14px 20px 16px; background: rgba(201,151,58,0.04); border-top: 1px solid rgba(201,151,58,0.18); }
      .adv-receipt-actions { display: flex; gap: 8px; justify-content: center; margin-bottom: 10px; flex-wrap: wrap; }
      .adv-rcpt-btn { display: inline-flex; align-items: center; gap: 7px; font-family: 'Cinzel', serif; font-size: 9px; font-weight: 700; letter-spacing: 1.5px; text-transform: uppercase; border: none; padding: 10px 18px; cursor: pointer; transition: all 0.25s; clip-path: polygon(10px 0,calc(100% - 10px) 0,100% 50%,calc(100% - 10px) 100%,10px 100%,0 50%); }
      .adv-rcpt-btn-print { background: linear-gradient(135deg,#1a0404,#3a0c0c); color: #e8c46a; box-shadow: 0 4px 12px rgba(26,4,4,0.3); }
      .adv-rcpt-btn-print:hover { filter: brightness(1.2); transform: translateY(-1px); }
      .adv-rcpt-btn-pdf { background: linear-gradient(135deg,#7a4e0c,#c9973a,#e8c46a,#c9973a,#7a4e0c); color: #1a0404; box-shadow: 0 4px 16px rgba(201,151,58,0.4); }
      .adv-rcpt-btn-pdf:hover:not(:disabled) { filter: brightness(1.08); transform: translateY(-1px); box-shadow: 0 8px 24px rgba(201,151,58,0.5); }
      .adv-rcpt-btn-pdf:disabled { opacity: 0.6; cursor: not-allowed; transform: none; }
      .adv-rcpt-btn-close { background: #fff; border: 1px solid rgba(201,151,58,0.35) !important; color: #5a3a1a; clip-path: none; border-radius: 20px; }
      .adv-rcpt-btn-close:hover { background: rgba(139,26,26,0.06); border-color: rgba(139,26,26,0.35) !important; color: #8b1a1a; }
      .adv-rcpt-spin { width: 10px; height: 10px; border-radius: 50%; border: 2px solid rgba(26,4,4,0.2); border-top-color: #1a0404; animation: spinIcon 0.7s linear infinite; display: inline-block; }
      .adv-receipt-note { font-size: 11px; color: #888; font-family: 'EB Garamond', serif; font-style: italic; text-align: center; }
      /* ══ CANCEL CONFIRM ══ */
      .adv-cancel-confirm-overlay { position: absolute; inset: 0; background: rgba(10,2,2,0.82); backdrop-filter: blur(4px); z-index: 10; display: flex; align-items: center; justify-content: center; padding: 20px; animation: toastIn 0.25s ease both; }
      .adv-confirm-box { background: linear-gradient(160deg,#FDF3E3,#faf0dc); padding: 28px 32px; border: 1px solid rgba(201,151,58,0.35); clip-path: polygon(0 14px,14px 0,calc(100% - 14px) 0,100% 14px,100% calc(100% - 14px),calc(100% - 14px) 100%,14px 100%,0 calc(100% - 14px)); max-width: 340px; text-align: center; }
      .adv-confirm-icon { font-size: 28px; color: #c9973a; margin-bottom: 8px; }
      .adv-confirm-box h3 { font-family: 'Cinzel Decorative', cursive; font-size: 15px; color: var(--crimson-deep); margin-bottom: 8px; }
      .adv-confirm-box p { font-family: 'EB Garamond', serif; font-style: italic; font-size: 14px; color: rgba(74,26,8,0.7); margin-bottom: 20px; line-height: 1.5; }
      .adv-confirm-box-actions { display: flex; gap: 10px; justify-content: center; }

      /* ══ TOAST ══ */
      .adv-toast-container { position: fixed; top: 28px; right: 28px; z-index: 2000; animation: toastIn 0.4s cubic-bezier(0.23,1,0.32,1) both; }
      @keyframes toastIn { from { opacity: 0; transform: translateX(60px) scale(0.95); } to { opacity: 1; transform: translateX(0) scale(1); } }
      .adv-toast-card { position: relative; width: 340px; background: linear-gradient(145deg,#FDF3E3,#faf0dc,#f5e8cc); border: 1px solid var(--border-color); clip-path: polygon(0 12px,12px 0,calc(100% - 12px) 0,100% 12px,100% calc(100% - 12px),calc(100% - 12px) 100%,12px 100%,0 calc(100% - 12px)); box-shadow: 0 12px 32px rgba(26,4,4,0.14), inset 0 1px 0 rgba(255,255,255,0.7); padding: 0 0 14px; overflow: hidden; }
      .adv-toast-top-bar { height: 3px; background: linear-gradient(90deg,transparent,var(--accent) 20%,var(--accent) 80%,transparent); margin-bottom: 16px; }
      .adv-toast-corner { position: absolute; width: 10px; height: 10px; border-color: rgba(201,151,58,0.45); border-style: solid; }
      .adv-toast-corner.tl { top: 12px; left: 12px; border-width: 1px 0 0 1px; }
      .adv-toast-corner.tr { top: 12px; right: 12px; border-width: 1px 1px 0 0; }
      .adv-toast-corner.bl { bottom: 8px; left: 12px; border-width: 0 0 1px 1px; }
      .adv-toast-corner.br { bottom: 8px; right: 12px; border-width: 0 1px 1px 0; }
      .adv-toast-medallion { position: absolute; left: 18px; top: 26px; width: 36px; height: 36px; border-radius: 50%; border: 1px solid; display: flex; align-items: center; justify-content: center; }
      .adv-toast-spin-ring { position: absolute; inset: -3px; border-radius: 50%; border: 1px dashed rgba(201,151,58,0.3); animation: slowSpin 8s linear infinite; }
      .adv-toast-icon-inner { font-size: 14px; position: relative; z-index: 1; }
      .adv-toast-content { padding: 0 40px 0 66px; }
      .adv-toast-label-row { display: flex; align-items: center; gap: 6px; margin-bottom: 5px; }
      .adv-toast-symbol { font-family: 'Cinzel Decorative', cursive; font-size: 9px; opacity: 0.7; }
      .adv-toast-title { font-family: 'Cinzel', serif; font-size: 10px; font-weight: 700; letter-spacing: 2px; text-transform: uppercase; flex: 1; }
      .adv-toast-rule { height: 1px; margin-bottom: 7px; }
      .adv-toast-message { font-family: 'EB Garamond', serif; font-style: italic; font-size: 14px; color: rgba(74,26,8,0.75); line-height: 1.5; }
      .adv-toast-bottom-rule { height: 1px; margin: 12px 20px 0; background: linear-gradient(90deg,transparent,rgba(201,151,58,0.25),transparent); }
      .adv-toast-close { position: absolute; top: 22px; right: 14px; background: none; border: none; font-size: 13px; cursor: pointer; opacity: 0.45; transition: opacity 0.2s; }
      .adv-toast-close:hover { opacity: 0.9; }

      /* ══ RESPONSIVE ══ */
      @media (max-width: 900px) {
        .adv-hero-rings { display: none; }
        .adv-vazhipadu-grid { grid-template-columns: repeat(2, 1fr); }
      }
      @media (max-width: 768px) {
        .adv-hero { padding: 22px 16px 0; border-radius: 0; }
        .adv-hero-title { font-size: 19px; }
        .adv-hero-medallion { width: 68px; height: 68px; }
        .adv-hero-om { font-size: 24px; }
        .adv-hero-logo-img { width: 40px; height: 40px; }
        .adv-stat-row { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; width: 100%; padding: 0 0 18px; }
        .adv-stat-card { min-width: unset; padding: 12px 10px; clip-path: none; border-radius: 8px; }
        .adv-toolbar { padding: 12px 14px; flex-wrap: wrap; }
        .adv-offerings-wrap { padding: 20px 14px 40px; background: #0e0101; }
        .adv-vazhipadu-grid { grid-template-columns: 1fr; gap: 14px; }
        .adv-modal-overlay { padding: 0; align-items: flex-end; }
        .adv-regal-modal { clip-path: none !important; border-radius: 22px 22px 0 0 !important; max-height: 93vh; border-bottom: none; width: 100%; max-width: 100% !important; }
        .adv-edit-body { padding: 16px 16px 28px; }
        .adv-edit-grid { grid-template-columns: 1fr; gap: 12px; }
        .adv-edit-grid .full-width { grid-column: span 1; }
        .adv-edit-wrap { clip-path: none; border-radius: 28px; }
        .adv-edit-save-btn { clip-path: none; border-radius: 28px; flex: 1; justify-content: center; }
        .adv-edit-actions .adv-btn { clip-path: none; border-radius: 28px; }
        .adv-add-person-btn { clip-path: none; border-radius: 28px; }
        .adv-person-block { clip-path: none; border-radius: 12px; }
        .adv-person-remove { clip-path: none; border-radius: 20px; }
        .adv-action-btn { clip-path: none; border-radius: 28px; }
        .adv-btn { clip-path: none; border-radius: 28px; }
        .adv-vazhipadu-card { clip-path: none; border-radius: 14px; }
        .adv-vz-book-btn { clip-path: none; border-radius: 20px; }
        .adv-toast-container { top: auto; bottom: 14px; right: 14px; left: 14px; }
        .adv-toast-card { width: 100%; clip-path: none; border-radius: 12px; }
      }
    `}</style>
  );
}